/**
 * About page copy.
 *
 * Same folder rule as landing.ts: every user-facing marketing string lives in
 * src/content/ so the banned-words gate scans one folder and a lawyer reviews
 * one folder. content.test.ts only matches DOUBLE-quoted literals — a string in
 * single quotes passes the gate by never being read.
 *
 * Register (Ned, 2026-08-12): personable, transparent, professional. NOT the
 * landing page's pub voice. "The analyst at the end of the bar" earns its place
 * on a hero where the job is to introduce a character; a reader who has clicked
 * through to About wants the plain version, and the founder passage in
 * particular reads as costume if it arrives in idiom.
 *
 * THE TIPPING-SERVICE POINT IS IMPLICIT, ON PURPOSE. The word "tips" appears
 * nowhere, and there is no "what KEV isn't" block, even though llms.ts carries
 * one. That file answers a crawler that will otherwise invent an answer; this
 * one is read by a person, and guardrails §2.3 is explicit that a defensive
 * banner on the product surface advertises the limitation and reads as nervous.
 * WHAT_YOU_GET.body says what KEV does when the evidence is thin, and stops —
 * that is the whole of it. Don't reinstate the negative version.
 *
 * Inherited from landing.ts's header and unchanged here:
 *   - No binary contrasts, no negative triples, no pull-quote endings.
 *   - Say what the product does, not what it isn't.
 *   - No data-provider names on marketing surfaces. Terms §7 holds them.
 *
 * Hard constraints:
 *   §1.3  banned words — enforced by content.test.ts
 *   §1.3  no win-rate, ROI or accuracy claim until the public ledger exists
 *   §2.3  no stake sizing, and no advertising that we don't do it
 *   §7    never invent a market price, line, or implied probability
 *
 * NO SPORT IS NAMED anywhere below, and COVERAGE points at /pricing instead.
 * Only the two NBA models are `build: "beta"` in config/catalogue.ts; AFL and
 * NFL fantasy are "soon" and NFL sport is off-roadmap. A sport named in prose
 * here is a claim that outlives the roadmap change that falsifies it, which is
 * the misleading-conduct exposure llms.ts already warns about.
 */

export const PURPOSE = {
  eyebrow: "About",
  heading: "Why KEV exists",
  body: [
    "The information that moves a betting market is expensive. Licensed data costs money, and building a model on top of it costs time. If you follow sport on weekends, most of that has sat out of reach.",
    "KEV opens it up. It runs statistical models across seasons of player and game data, then answers questions about them in plain English. Ask about a player's night or a match-up and you get the model's number, with the reasoning attached.",
    "Reading it takes no background in statistics.",
  ],
} as const;

export const WHAT_YOU_GET = {
  eyebrow: "What You Get",
  heading: "You can check the working",
  body: [
    "When the evidence is thin, KEV says so and stops. A projection off four games looks the same as one off four seasons, so KEV tells you which you've got.",
  ],
} as const;

/* Unnamed on purpose for now (Ned, 2026-08-12): third person was the call, a
   name to publish was not given, and nothing else on the site names a person.
   Adding one is an edit to body[0] and nothing else.

   "Data and analytics" is the founder's own wording. It was "statistical
   modelling and analytics" in the first draft and that was wrong — the models
   are the product's claim, made in PURPOSE.body, not a claim about a CV. */
export const FOUNDER = {
  eyebrow: "The Founder",
  heading: "Where KEV came from",
  body: [
    "KEV began as a personal project. Its founder works in data and analytics, and spent years building predictive sports models in his own time, testing whether the things people repeat about a game hold up against the numbers.",
    "Often they didn't. The explanations were worth more than the numbers.",
    "KEV is that project, opened up for anyone who wants to ask it something.",
  ],
} as const;

/* Not decoration. llms.ts holds the rule this band implements: no surface
   describes a sport as available unless /pricing lists it, because that page
   derives its list from the live catalogue and this one cannot. */
export const COVERAGE = {
  eyebrow: "Coverage",
  heading: "What's live today",
  cta: "See pricing",
} as const;
